import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { expand, map, reduce } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { Note, NoteAdapter } from '../models/note.model';


@Injectable()
export class NoteService {
    private api: string;

    constructor(private http: HttpClient, private adapter: NoteAdapter) {
        this.api = `${environment.apiRoot}${environment.apiEndpoints.fundingRequests}/{:id}${environment.apiEndpoints.notes}`;
    }

    /**
     * Get all Notes for a given Funding Request
     */
    list(requestId: string): Observable<Note[]> {
        const endpoint = this.api.replace('{:id}', requestId);
        return this.getItems(endpoint).pipe(
            expand(data => data.next ? this.getItems(endpoint, data.next) : of()),
            reduce<{next: string, results: Note[]}, Note[]>((acc, data) => acc.concat(data.results), []),
        );
    }

    private getItems(endpoint: string, startKey: string = ''): Observable<{next: string, results: Note[]}> {
        if (startKey) {
            endpoint += `?startKey=${startKey}`;
        }
        return this.http.get<any>(endpoint).pipe(
            map(response => {
                const nextId = ('lastEvaluatedKey' in response) ? response.lastEvaluatedKey : null;
                return {
                    next: nextId,
                    results: response.notes.map((item: any) => this.adapter.adapt(item))
                };
            })
        );
    }

    /**
     * Get a single Note for a given Funding Request
     */
    get(requestId: string, noteId: number): Observable<Note> {
        const endpoint = `${this.api.replace('{:id}', requestId)}/${noteId}`;
        return requestId && noteId ?
            this.http.get<any>(endpoint).pipe(
                map(response => this.adapter.adapt(response.note)),
            ) :
            of<Note>();
    }

    /**
     * Create a new Note for a given Funding Request
     */
    put(requestId: string, note: Note): Observable<Note> {
        const endpoint = this.api.replace('{:id}', requestId);
        const data = {
            comment: note.comment,
            username: note.userId,
            timestamp: note.date?.getTime()
        };
        return this.http.put<any>(endpoint, data).pipe(map(() => note));
    }

    /**
     * Edit a Note for a given Funding Request
     */
    update(requestId: string, noteId: number, newComment: string, editTimestamp: number = -1): Observable<Note> {
        const endpoint = `${this.api.replace('{:id}', requestId)}/${noteId}`;
        const data = {
            comment: newComment,
            timestamp: editTimestamp > 0 ? editTimestamp : new Date().getTime()
        };
        return requestId && noteId ?
            this.http.post<any>(endpoint, data).pipe(
                map(response => this.adapter.adapt(response.note)),
            ) :
            of<Note>();
    }

    /**
     * Delete a Note for a given Funding Request
     */
    delete(requestId: string, noteId: number): Observable<boolean> {
        const endpoint = `${this.api.replace('{:id}', requestId)}/${noteId}`;
        return this.http.delete<any>(endpoint).pipe(map(() => true));
    }
}
